/**
 * K4c.2 — one security we placed with a landlord, and its documents.
 *
 * DELIBERATELY THIN
 * -----------------
 * Six facts, and that is all there is. `LandlordDepositItem` says why in its
 * own docstring: "There is deliberately no paid date, release date or status:
 * those columns do not exist, and inventing them in the response would imply
 * a history the database cannot back." A unit carries a type, an amount, a
 * provider and a premium — no lifecycle, no confirmation, no dates. So this
 * modal shows six fields and no status badge, because there is no status to
 * show and a fabricated one would be worse than a short list.
 *
 * The documents are therefore the substance of this screen, not a supplement.
 *
 * NOT AN EDITOR
 * -------------
 * `landlord_deposit_type/amount/annual_premium/provider` are edited in the
 * unit form. Adding a second editor here would split ownership of four
 * columns across two surfaces — the exact shape K4x.3b and K4x.3d removed on
 * the tenant side. The "Einheit öffnen" link goes to the one place that owns
 * them.
 *
 * That link is also the navigation the row used to offer directly: before
 * K4c.2, "Details" was a `<Link>` to /admin/units/{id}. It is still a real
 * `<Link>` — middle-click, new tab and copy-link all still work — just one
 * click further along, so the row has one action instead of two.
 *
 * WHY `insurance` IS NOT HERE
 * ---------------------------
 * A unit's documents include the category `insurance`, and it is NOT shown.
 * It predates the deposit namespace by years and means any policy at all —
 * building, liability, contents. Treating it as a security document would be
 * this screen asserting something about data nobody entered as one. K4c.1
 * reserved `landlord_deposit_*` precisely so the deposit view could be exact
 * instead of guessing.
 */
import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";

import { ResponsiveModal } from "../../../components/ui/responsive-modal";
import {
  deleteAdminUnitDocument,
  fetchAdminUnitDocumentDownloadUrl,
  fetchAdminUnitDocuments,
  uploadAdminUnitDocument,
} from "../../../api/adminData";
import { chf, chfPrecise } from "../../../components/admin/companyFinance/financeFormat";
import DepositDocumentsList from "./DepositDocumentsList";
import { depositTypeLabel, providerLabel } from "./depositsLabels";
import {
  isLandlordDepositDocument,
  LANDLORD_DEPOSIT_DOCUMENT_CATEGORIES,
  landlordDepositDocumentCategoryLabel,
} from "./landlordDepositDocumentCategories";

function Fact({ label, value, testId }) {
  if (value == null || value === "") return null;
  return (
    <div className="min-w-0">
      <dt className="text-[11px] text-muted-foreground">{label}</dt>
      <dd
        data-testid={testId}
        className="break-words text-[13px] font-medium text-foreground"
      >
        {value}
      </dd>
    </div>
  );
}

export default function LandlordDepositDetailModal({
  item,
  open,
  onOpenChange,
}) {
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [deletingId, setDeletingId] = useState(null);

  const unitId = item?.unit_id || null;

  const load = useCallback(async () => {
    if (!unitId) return;
    setLoading(true);
    setError(null);
    try {
      const rows = await fetchAdminUnitDocuments(unitId);
      setDocs(Array.isArray(rows) ? rows : []);
    } catch (err) {
      setError(err?.message || "Dokumente konnten nicht geladen werden.");
    } finally {
      setLoading(false);
    }
  }, [unitId]);

  /** On demand only — see the tenant modal for why this is load-bearing. */
  useEffect(() => {
    if (!open) {
      setDocs([]);
      setError(null);
      return;
    }
    load();
  }, [open, load]);

  // The filter is the whole reason K4c.1 exists: a unit's document list is
  // mixed, and only the reserved namespace belongs in a deposit view.
  const visible = docs.filter(isLandlordDepositDocument);

  const handleUpload = async (file, category) => {
    if (!unitId) return;
    setUploading(true);
    try {
      await uploadAdminUnitDocument(unitId, file, { category });
      toast.success("Dokument hochgeladen.");
      await load();
    } catch (err) {
      toast.error(err?.message || "Upload fehlgeschlagen.");
    } finally {
      setUploading(false);
    }
  };

  const handleDownload = async (doc) => {
    try {
      const data = await fetchAdminUnitDocumentDownloadUrl(doc.id);
      if (data?.url) window.open(data.url, "_blank", "noopener,noreferrer");
    } catch (err) {
      toast.error(err?.message || "Download konnte nicht gestartet werden.");
    }
  };

  const handleDelete = async (doc) => {
    if (!window.confirm("Dokument wirklich löschen?")) return;
    setDeletingId(doc.id);
    try {
      await deleteAdminUnitDocument(doc.id);
      toast.success("Dokument gelöscht.");
      await load();
    } catch (err) {
      toast.error(err?.message || "Löschen fehlgeschlagen.");
    } finally {
      setDeletingId(null);
    }
  };

  if (!item) return null;

  const premium = Number(item.annual_premium_chf);
  const unitLabel =
    [item.title, item.short_unit_id].filter(Boolean).join(" · ") || "Einheit";
  const place = [item.address, item.city].filter(Boolean).join(", ");

  return (
    <ResponsiveModal
      open={open}
      onOpenChange={onOpenChange}
      title={`Sicherheitsleistung – ${unitLabel}`}
      description="Angaben aus der Einheit. Bearbeitet wird die Sicherheit im Einheiten-Formular."
      contentClassName="max-w-[560px]"
    >
      <div
        data-testid="landlord-deposit-detail"
        className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto pr-1 text-foreground"
      >
        <div className="space-y-3">
          <dl className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <Fact
              label="Einheit"
              value={unitLabel}
              testId="landlord-deposit-detail-unit"
            />
            <Fact
              label="Adresse"
              value={place || null}
              testId="landlord-deposit-detail-place"
            />
            <Fact
              label="Verwaltung"
              value={item.landlord_name}
              testId="landlord-deposit-detail-landlord"
            />
            <Fact
              label="Sicherheitsart"
              value={depositTypeLabel(item.deposit_type)}
              testId="landlord-deposit-detail-type"
            />
            <Fact
              label="Betrag"
              value={chf(item.deposit_amount_chf)}
              testId="landlord-deposit-detail-amount"
            />
            <Fact
              label="Anbieter"
              value={providerLabel(item.provider, item.deposit_type)}
              testId="landlord-deposit-detail-provider"
            />
            <Fact
              label="Prämie p.a."
              value={premium > 0 ? chfPrecise(premium) : null}
              testId="landlord-deposit-detail-premium"
            />
          </dl>

          {item.unit_id ? (
            <Link
              to={`/admin/units/${item.unit_id}`}
              data-testid="landlord-deposit-detail-link"
              className="inline-flex items-center gap-1 rounded-md px-1.5 py-1 text-[12px] font-medium text-muted-foreground underline-offset-2 transition-colors hover:text-foreground hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              Einheit öffnen
            </Link>
          ) : null}
        </div>

        <div className="border-t border-border pt-3">
          <DepositDocumentsList
            documents={visible}
            loading={loading}
            error={error}
            onRetry={load}
            categoryOptions={LANDLORD_DEPOSIT_DOCUMENT_CATEGORIES}
            categoryLabel={landlordDepositDocumentCategoryLabel}
            onUpload={handleUpload}
            onDownload={handleDownload}
            onDelete={handleDelete}
            uploading={uploading}
            deletingId={deletingId}
            testIdPrefix="landlord-deposit"
          />
        </div>
      </div>
    </ResponsiveModal>
  );
}
